// src/components/games/diceMatch/types.ts

export type DiceType = 'd4' | 'd6' | 'd8' | 'd10' | 'd12' | 'd20' | 'd100' | 'sparkle';

export interface DiceSlot {
  slotNumber: number; // 1, 2, 3, 4, etc. - determines generation probabilities
}

// Generation probability weights for each slot tier
// Each slot number has different weights for generating dice types
export const SLOT_GENERATION_WEIGHTS: Record<number, Partial<Record<DiceType, number>>> = {
  1: { 'd4': 100 }, // Slot 1: Always d4
  2: { 'd4': 100 }, // Slot 2: Always d4
  3: { 'd4': 100 }, // Slot 3: Always d4
  4: { 'd4': 70, 'd6': 30 }, // Slot 4: 70% d4, 30% d6
  5: { 'd4': 50, 'd6': 40, 'd8': 10 }, // Slot 5: 50% d4, 40% d6, 10% d8
  6: { 'd4': 30, 'd6': 40, 'd8': 25, 'd10': 5 }, // Slot 6: better variety
  7: { 'd6': 40, 'd8': 35, 'd10': 20, 'd12': 5 }, // Slot 7: no more d4s
  8: { 'd6': 20, 'd8': 35, 'd10': 30, 'd12': 15 }, // Slot 8: higher tiers more likely
  9: { 'd8': 30, 'd10': 30, 'd12': 25, 'd20': 15 }, // Slot 9: approaching d20s
  10: { 'd10': 25, 'd12': 30, 'd20': 35, 'd100': 10 }, // Slot 10: elite tier
};

export interface GridCell {
  diceType: DiceType | null;
  isLocked: boolean; // sparkles lock the space
  id: string; // unique identifier for animations
}

export interface TrinketEffects {
  scoreMultiplier: number;
  rerollsRemaining: number;
  undoRemaining: number;
  guaranteedMerges: number;
  skipDieLevel: boolean;
  wildDieNext: boolean;
  doubleDieNext: boolean;
}

export interface GameStateSnapshot {
  grid: GridCell[][];
  score: number;
  movesRemaining: number;
  diceSlots: DiceSlot[];
  availableDice: DiceType[];
  inGameCurrency: number;
  currentRoll: DiceType | null;
  sparklesCreated: number;
  trinketEffects: TrinketEffects;
}

export interface GameState {
  grid: GridCell[][];
  score: number;
  movesRemaining: number;
  diceSlots: DiceSlot[]; // Permanent slots that regenerate dice
  availableDice: DiceType[]; // Current pool of dice generated from slots
  inGameCurrency: number; // Resource type 1 - earned during play
  currentRoll: DiceType | null; // The die waiting to be placed
  gameStatus: 'active' | 'won' | 'lost';
  sparklesCreated: number;
  currencyMultiplier: number; // Multiplier for currency earned (from meta upgrades)
  trinketEffects: TrinketEffects; // Active trinket effects for this game
  moveHistory: GameStateSnapshot[]; // For undo functionality
}

export interface GameConfig {
  gridSize: number; // e.g., 4 for 4x4, 5 for 5x5
  startingMoves: number;
  startingSlots: number; // Number of dice slots to start with
  currencyMultiplier: number; // 1.0 = 100%, 1.5 = 150%, etc.
}

export interface InGameUpgrade {
  id: string;
  name: string;
  description: string;
  cost: number; // in-game currency
  type: 'moves' | 'slot' | 'grid';
  value: number; // how much it adds
  icon: string;
}

export interface MetaUpgrade {
  id: string;
  name: string;
  description: string;
  cost: number; // meta currency
  level: number;
  maxLevel: number;
  isPurchased: boolean;
  effect: {
    type: 'starting_moves' | 'starting_slots' | 'starting_grid' | 'currency_multiplier';
    value: number;
  };
}

export interface GameResult {
  score: number;
  highestDie: DiceType;
  sparklesCreated: number;
  movesUsed: number;
  metaCurrencyEarned: number;
}

export const DICE_PROGRESSION: DiceType[] = ['d4', 'd6', 'd8', 'd10', 'd12', 'd20', 'd100', 'sparkle'];

export const DICE_VALUES: Record<DiceType, number> = {
  'd4': 4,
  'd6': 6,
  'd8': 8,
  'd10': 10,
  'd12': 12,
  'd20': 20,
  'd100': 100,
  'sparkle': 1000,
};

export const DICE_SCORE_VALUES: Record<DiceType, number> = {
  'd4': 10,
  'd6': 25,
  'd8': 50,
  'd10': 100,
  'd12': 250,
  'd20': 500,
  'd100': 1000,
  'sparkle': 5000,
};

export const DEFAULT_CONFIG: GameConfig = {
  gridSize: 4,
  startingMoves: 20,
  startingSlots: 1,
  currencyMultiplier: 1.0,
};

export const IN_GAME_UPGRADES: InGameUpgrade[] = [
  {
    id: 'add_moves_5',
    name: '+5 Moves',
    description: 'Add 5 more moves to your game',
    cost: 50,
    type: 'moves',
    value: 5,
    icon: '⏱️',
  },
  {
    id: 'add_slot_1',
    name: 'Add Dice Slot',
    description: 'Permanently add a new dice slot with better generation chances',
    cost: 100,
    type: 'slot',
    value: 1,
    icon: '🎰',
  },
];
